/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import Stack from "@mui/material/Stack";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import moment from "moment";
import Div from "@jumbo/shared/Div";
import Loader from "@jumbo/components/Loader";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { userType } from "app/helpers/userType";
import {
  createPartyNumber,
  deletePartyNumber,
  exportPartyNumberList,
  getPartyNumberList,
  updatePartyNumber,
} from "app/redux/slice/partyNumberSlice";
import { downloadListExcel } from "app/helpers/csvExport";
import { getUserList } from "app/redux/slice/userSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import UpdatePartyNumber from "./update-party-number";
import { PARTY_NUMBER_ADD } from "app/routes/constant";
import { useNavigate } from "react-router-dom";

const PartyNumber = () => {
  const { isOpen, onClose, stageAction, actionProps } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { isMA } = userType();
  const partyNumberData = useSelector((state) => state?.partyNumber);
  const userData = useSelector((state) => state?.user);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [belongsToFilter, setBelongsToFilter] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Guard against double delete invocation
  const isDeletingRef = useRef(false);

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "partyNumber" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-party-number") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-party-number") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-party-number") {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    { id: "name", label: "Party Name", sort: true },
    {
      id: "phoneNumbers",
      label: "Phone Numbers",
      sort: false,
      render: (row) =>
        Array.isArray(row?.phoneNumbers)
          ? row.phoneNumbers.join(", ")
          : row?.phoneNumbers || "-",
    },
    { id: "city", label: "City", sort: true },
    {
      id: "belongsTo",
      label: "Belongs To",
      sort: true,
      render: (row) =>
        row?.belongsToUser?.name || row?.belongsToUser?.email || "-",
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 130,
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              stageAction(row);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];

  const handleSubmit = async (data) => {
    const payload = {
      name: data?.name?.trim(),
      phoneNumbers: Array.isArray(data?.phoneNumbers)
        ? data.phoneNumbers.map((p) => p.trim())
        : [],
      city: data?.city?.trim(),
      belongsTo: data?.belongsTo?._id || data?.belongsTo,
    };

    const response = await dispatch(
      actionProps?._id
        ? updatePartyNumber({ id: actionProps._id, data: payload })
        : createPartyNumber(payload),
    ).unwrap();

    if (response.status === 200 || response.status === 201) {
      enqueueSnackbar(response.data?.responseMessage, { variant: "success" });
      getData();
      onClose();
    } else {
      enqueueSnackbar(response.data?.responseMessage, { variant: "error" });
    }
  };

  const deletePartyNumberHandler = () => {
    if (isDeletingRef.current) return;
    isDeletingRef.current = true;

    dispatch(deletePartyNumber(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        isDeletingRef.current = false;
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, { variant: "success" });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, { variant: "error" });
        }
      })
      .catch(() => {
        isDeletingRef.current = false;
      });
  };

  const getData = () => {
    dispatch(
      getPartyNumberList({
        page: page,
        perPage: rowsPerPage,
        ...activeFilters(),
      }),
    );
  };

  // The filters only - no page/perPage. Shared by the list and the download so
  // the file can never be filtered differently from the table.
  const activeFilters = () => ({
    ...(search && { search: search?.trim() }),
    ...(belongsToFilter && { belongsTo: belongsToFilter._id }),
    ...(orderBy !== "" && { field: orderBy, sort: order === "asc" ? 1 : -1 }),
  });

  const handleDownloadExcel = async () => {
    const total = partyNumberData?.total || 0;
    if (!total) {
      enqueueSnackbar("There is nothing to download for these filters.", {
        variant: "info",
      });
      return;
    }

    setDownloading(true);
    try {
      const res = await dispatch(
        exportPartyNumberList({
          ...activeFilters(),
          // Every matching row in a single page: the file is the whole filtered
          // list, not the page on screen. The spare 200 covers rows added
          // between the count the table knows and this request.
          page: 1,
          perPage: total + 200,
        }),
      ).unwrap();

      if (res?.status !== 200) {
        enqueueSnackbar(
          res?.data?.responseMessage || "Could not download the Excel file.",
          { variant: "error" },
        );
        return;
      }

      const rows = res.data?.data?.partyNumberList ?? [];
      if (!rows.length) {
        enqueueSnackbar("There is nothing to download for these filters.", {
          variant: "info",
        });
        return;
      }

      downloadListExcel({
        fileName: `party-number-list-${moment().format("DD-MM-YYYY")}.xlsx`,
        sheetName: "Party Numbers",
        columns: [
          { header: "Sr No", width: 8, value: (row, index) => index + 1 },
          { header: "Party Name", width: 30, value: (row) => row?.name },
          {
            header: "Phone Numbers",
            width: 34,
            // Kept as text, with every number the party has - a phone number
            // written as a number loses a leading zero.
            value: (row) =>
              Array.isArray(row?.phoneNumbers)
                ? row.phoneNumbers.join(", ")
                : row?.phoneNumbers,
          },
          { header: "City", width: 18, value: (row) => row?.city },
          {
            header: "Belongs To",
            width: 24,
            value: (row) => row?.belongsToUser?.name || row?.belongsToUser?.email,
          },
          {
            header: "Created On",
            width: 14,
            value: (row) =>
              row?.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "",
          },
        ],
        rows,
        filters: [
          ["Search", search?.trim() || "All"],
          [
            "Belongs To",
            belongsToFilter?.name || belongsToFilter?.email || "All",
          ],
          [
            "Sorted by",
            orderBy ? `${orderBy} (${order === "asc" ? "A-Z" : "Z-A"})` : "-",
          ],
          ["Records", rows.length],
          ["Downloaded on", moment().format("DD-MM-YYYY hh:mm A")],
        ],
      });

      enqueueSnackbar(`${rows.length} record(s) downloaded.`, {
        variant: "success",
      });
    } finally {
      setDownloading(false);
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleSearch = () => {
    handleChangePage(0, 0);
    setSearch(searchInput);
  };

  const handleClearAll = () => {
    setSearchInput("");
    setSearch("");
    setBelongsToFilter(null);
    handleChangePage(0, 0);
  };

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, order, orderBy, search, belongsToFilter]);

  useEffect(() => {
    dispatch(getUserList({ sort: 1, field: "name" }));
    getPermission();
  }, []);

  return (
    <>
      <Card sx={{ display: "flex", mb: 3 }}>
        <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
          <CardContent>
            <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by name, phone, city"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  InputProps={{
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchInput("");
                            setSearch("");
                            handleChangePage(0, 0);
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <JumboAutoComplete
                  options={userData?.list || []}
                  label="Belongs To"
                  size="small"
                  getOptionLabel={(option) =>
                    option?.name || option?.email || ""
                  }
                  name="belongsToFilter"
                  value={belongsToFilter}
                  onChange={(evt, value) => {
                    setBelongsToFilter(value);
                    handleChangePage(0, 0);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearAll}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Div>
      </Card>
      {partyNumberData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Party Number List
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: { xs: 1, md: 0 } }}
                >
                  <LoadingButton
                    onClick={handleDownloadExcel}
                    loading={downloading}
                    startIcon={<FileDownloadIcon />}
                    variant="outlined"
                    // The whole filtered list, not just the page on screen.
                    title="Download every row matching the current filters"
                  >
                    Download Excel
                  </LoadingButton>
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => {
                        navigate(PARTY_NUMBER_ADD);
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Party Number
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={partyNumberData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={partyNumberData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>

          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <UpdatePartyNumber
                value={actionProps}
                onClose={onClose}
                handleSubmit={handleSubmit}
              />
            }
            title={
              actionProps?._id ? "Update Party Number" : "Add Party Number"
            }
            fullWidth={true}
            maxWidth={"md"}
            actions={<></>}
          />

          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this party number?"
            onConfirm={deletePartyNumberHandler}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default PartyNumber;
